import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogService } from '../service/blogService';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MetalRateStrip from '../components/MetalRateStrip';
import './BlogDetailPage.css';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchPost = async () => {
      setLoading(true);
      const data = await blogService.getPostBySlug(slug);
      setPost(data);
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(`Read this B2B jewellery trade analysis on JMI: "${post?.title}"\n${window.location.href}`);
    window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
  };

  // Convert markdown body text into clean formatted HTML paragraphs
  const renderFormattedContent = (contentStr) => {
    if (!contentStr) return null;

    const sections = contentStr.split('\n\n');
    return sections.map((sec, idx) => {
      const trimmed = sec.trim();
      if (!trimmed) return null;

      if (trimmed.startsWith('### ')) {
        return <h3 key={idx} className="article-h3">{trimmed.replace('### ', '')}</h3>;
      }
      if (trimmed.startsWith('## ')) {
        return <h2 key={idx} className="article-h2">{trimmed.replace('## ', '')}</h2>;
      }
      if (trimmed.startsWith('#### ')) {
        return <h4 key={idx} className="article-h4">{trimmed.replace('#### ', '')}</h4>;
      }
      if (trimmed.startsWith('> ')) {
        return (
          <blockquote key={idx} className="article-blockquote">
            {trimmed.replace('> ', '')}
          </blockquote>
        );
      }
      if (trimmed.startsWith('```')) {
        const codeClean = trimmed.replace(/```[a-z]*\n?/g, '').replace(/```$/g, '');
        return (
          <pre key={idx} className="article-codeblock">
            <code>{codeClean}</code>
          </pre>
        );
      }
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        const lines = trimmed.split('\n');
        return (
          <ul key={idx} className="article-list">
            {lines.map((li, liIdx) => (
              <li key={liIdx}>{li.replace(/^[-*]\s+/, '')}</li>
            ))}
          </ul>
        );
      }
      if (/^\d+\.\s/.test(trimmed)) {
        const lines = trimmed.split('\n');
        return (
          <ol key={idx} className="article-ordered-list">
            {lines.map((li, liIdx) => (
              <li key={liIdx}>{li.replace(/^\d+\.\s+/, '')}</li>
            ))}
          </ol>
        );
      }

      return <p key={idx} className="article-p">{trimmed}</p>;
    });
  };

  return (
    <div className="app-wrapper blog-detail-wrapper">
      <MetalRateStrip />
      <Navbar />

      <main className="blog-detail-main">
        {loading ? (
          <div className="blog-loading-state">
            <div className="spinner"></div>
            <span>Loading article content...</span>
          </div>
        ) : !post ? (
          <div className="blog-empty-state">
            <h2>Article Not Found</h2>
            <p>The requested jewellery trade analysis could not be located.</p>
            <Link to="/blog" className="btn btn-primary">
              Return to All Articles
            </Link>
          </div>
        ) : (
          <article className="article-reading-container">
            {/* Breadcrumb Navigation */}
            <div className="article-breadcrumbs">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/blog">Industry Journal</Link>
              <span>/</span>
              <span className="current-crumb">{post.category}</span>
            </div>

            {/* Header Area */}
            <header className="article-header">
              <div className="article-category-pill">{post.category}</div>
              <h1 className="article-headline">{post.title}</h1>
              <p className="article-summary-lead">{post.excerpt}</p>

              <div className="article-author-meta-bar">
                <div className="author-meta-left">
                  <div className="author-avatar-big">{post.author?.[0] || 'J'}</div>
                  <div>
                    <div className="author-fullname">{post.author}</div>
                    <div className="author-credentials">{post.authorRole || 'JMI Industry Analyst'}</div>
                  </div>
                </div>

                <div className="article-pub-stats">
                  <div className="pub-date">
                    {new Date(post.publishedAt || post.createdAt || Date.now()).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </div>
                  <div className="pub-readtime">⏱️ {post.readTime || '6 min read'}</div>
                </div>
              </div>
            </header>

            {/* Cover Image */}
            {post.coverImage && (
              <div className="article-featured-media">
                <img src={post.coverImage} alt={post.title} />
              </div>
            )}

            {/* Article Body Content */}
            <div className="article-body-content">
              {renderFormattedContent(post.content)}
            </div>

            {/* Tags Row */}
            {post.tags && post.tags.length > 0 && (
              <div className="article-tags-row">
                <span className="tags-label">Related Topics:</span>
                {post.tags.map((tag, tIdx) => (
                  <span key={tIdx} className="article-tag-pill">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Share & Conversion Section */}
            <div className="article-share-box">
              <div className="share-text">Share this report with your jewellery procurement team:</div>
              <div className="share-buttons">
                <button type="button" className="btn-share-wa" onClick={handleWhatsAppShare}>
                  💬 Share on WhatsApp
                </button>
                <button type="button" className="btn-share-copy" onClick={handleShare}>
                  {copied ? '✓ Link Copied!' : '🔗 Copy Article Link'}
                </button>
              </div>
            </div>

            {/* Onboarding Call to Action */}
            <div className="article-cta-box">
              <div className="cta-icon">💎</div>
              <div>
                <h3>Ready to Trade with Verified BIS Manufacturers?</h3>
                <p>JMI provides locked bullion pricing, insured regional vault logistics, and digital HUID validation on every wholesale order.</p>
              </div>
              <Link to="/apply" className="btn btn-primary">
                Apply for Verified Access →
              </Link>
            </div>
          </article>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
